
<template>
  <div id="component-chat-log" class="m-8" v-if="chatData">
    <div v-for="(msg, index) in chatData.messages" class="msg-grp-container" :key="index">
      <!-- If previous msg is older than current time -->
      <template v-if="chatData.messages[index - 1]">
        <vs-divider
          v-if="!isSameDay(msg.timestamp, chatData.messages[index - 1].timestamp)"
          class="msg-time text-white"
        >
          <span>{{ toDate(msg.timestamp) }}</span>
        </vs-divider>
        <div
          class="spacer mt-8"
          v-if="
            chatData.messages[index - 1].isSystemMessage ||
            !hasSentPreviousMsg(chatData.messages[index - 1].didSend, msg.didSend)
          "
        ></div>
      </template>

      <!--  System message content -->
      <div v-if="msg.isSystemMessage" class="flex-grow">
        <vs-divider>
          <div class="flex items-center flex-col">
            <b>{{ msg.text }}</b>
            <span>{{ msg.timestamp | formatTime }}</span>
          </div>
        </vs-divider>
      </div>

      <div v-else class="flex items-start" :class="[{ 'flex-row-reverse': msg.didSend }]">
        <template v-if="chatData.messages[index - 1]">
          <template
            v-if="
              chatData.messages[index - 1].isSystemMessage ||
              !hasSentPreviousMsg(chatData.messages[index - 1].didSend, msg.didSend) ||
              !isSameDay(msg.timestamp, chatData.messages[index - 1].timestamp)
            "
          >
            <vs-avatar
              size="40px"
              class="border-2 shadow border-solid border-white m-0 flex-shrink-0"
              :class="msg.didSend ? 'sm:ml-5 ml-3' : 'sm:mr-5 mr-3'"
              :color="msg.didSend ? venue(chatData.venueId).style.colorBackground : ''"
              :src="senderImg(msg.didSend, chatData.venueId)"
            ></vs-avatar>
          </template>
        </template>

        <template v-if="index == 0">
          <vs-avatar
            size="40px"
            class="border-2 shadow border-solid border-white m-0 flex-shrink-0"
            :class="msg.didSend ? 'sm:ml-5 ml-3' : 'sm:mr-5 mr-3'"
            :src="senderImg(msg.didSend, chatData.venueId)"
          ></vs-avatar>
        </template>

        <!--  Chat message content -->
        <template v-if="chatData.messages[index - 1]">
          <div
            class="mr-16"
            v-if="
              !(
                chatData.messages[index - 1].isSystemMessage ||
                !hasSentPreviousMsg(chatData.messages[index - 1].didSend, msg.didSend) ||
                !isSameDay(msg.timestamp, chatData.messages[index - 1].timestamp)
              )
            "
          ></div>
        </template>

        <div v-if="msg.text && isPureEmojiString(msg.text)">
          <span class="text-4xl">{{ msg.text }}</span>
        </div>

        <div
          v-else-if="msg.text"
          class="
            msg
            break-words
            relative
            shadow-md
            rounded
            py-3
            px-4
            mb-2
            rounded-lg
            max-w-sm
            whitespace-pre-wrap
          "
          :class="{
            'bg-primary-gradient text-white': msg.didSend,
            'border border-solid border-transparent bg-white': !msg.didSend,
          }"
        >
          <span>{{ msg.text }}</span>
        </div>

        <vs-row v-else class="mb-2">
          <div
            v-for="(imgSrc, idx) in msg.attachments"
            :key="idx"
            class="chat-image-wrapper m-2"
            :class="{ small: msg.attachments.length > 1 }"
          >
            <img v-img:messengerAttachments class="chat-image" :src="imgSrc" />
          </div>
        </vs-row>
      </div>
    </div>

    <div class="flex items-start justify-end mr-4" v-if="isSending">
      <div
        class="
          animate-pulse
          msg
          break-words
          shadow-md
          rounded
          py-3
          px-4
          mb-2
          rounded-lg
          max-w-sm
          bg-primary-gradient
          text-white
          mr-12
        "
      >
        <span>Sending...</span>
      </div>
    </div>
  </div>
</template>

<script>
import emojiRegex from "emoji-regex";

export default {
  props: {
    isSending: { type: Boolean, required: true },
    userId: {
      type: String,
      required: true,
    },
  },
  computed: {
    chatData() {
      return this.$store.getters["chat/activeConversation"];
    },
    venueLogo() {
      return venueId => this.$store.getters["venues/venueLogo"](venueId);
    },
    venue() {
      return venueId => this.$store.getters["venues/venue"](venueId);
    },
    senderImg() {
      return (didSendByActiveUser, venueId) => {
        if (didSendByActiveUser) return this.venueLogo(venueId);
        else return this.$store.getters["chat/activeConversation"].messengerUser.profilePic;
      };
    },
    hasSentPreviousMsg() {
      return (last_sender, current_sender) => last_sender === current_sender;
    },
  },
  methods: {
    isPureEmojiString(msg) {
      const regex = emojiRegex();
      const withoutEmojis = msg.replace(regex, "");
      return msg.length != 0 && withoutEmojis.length == 0; // Emoji if it only contained emojis
    },
    isSameDay(time_to, time_from) {
      const date_time_to = new Date(Date.parse(time_to));
      const date_time_from = new Date(Date.parse(time_from));
      return (
        date_time_to.getFullYear() === date_time_from.getFullYear() &&
        date_time_to.getMonth() === date_time_from.getMonth() &&
        date_time_to.getDate() === date_time_from.getDate()
      );
    },
    toDate(time) {
      const locale = "en-us";
      const date_obj = new Date(Date.parse(time));
      const monthName = date_obj.toLocaleString(locale, {
        month: "short",
      });
      return `${date_obj.getDate()} ${monthName}`;
    },
    scrollToBottom() {
      this.$nextTick(() => {
        this.$parent.$el.scrollTop = this.$parent.$el.scrollHeight;
      });
    },
  },
  updated() {
    this.scrollToBottom();
  },
  mounted() {
    this.scrollToBottom();
  },
};
</script>


<style lang="scss" scoped>
.chat-image {
  max-width: 300px;
  display: block;
}

.chat-image-wrapper {
  overflow: hidden;

  img {
    border-radius: 1rem !important;
    max-width: 256px;
  }

  &.small img {
    max-width: 128px;
  }
}

.sending-message {
  position: absolute;
  left: 0;
  right: 0;
  transition: all 0.2s ease-in-out;
  transition-delay: 10ms;
  transform: translateY(60px);
  display: flex;

  &.active {
    transform: translateY(0px);
  }
}
</style>